<template>
  <div class="download-agent-root" :dir="isRtl ? 'rtl' : 'ltr'">
    <!-- Desktop Layout -->
    <div class="for-web page">
      <img class="page-bg" :src="bgDesktop" alt="" />
      <div class="page-inner">
        <section class="hero hero-web">
          <h1 class="headline">{{ t.k001 }}</h1>
          <div class="subtitle-card">
            <p>{{ t.k006 }}</p>
          </div>
        </section>
        <section class="phone-section phone-section-web">
          <img class="phone-img" :src="bgPhone" alt="" />
        </section>
      </div>
      <section class="bottom-panel bottom-panel-web">
        <div class="download-cards download-cards-web">
          <div class="download-card" v-for="card in cards" :key="card.storeType">
            <img class="app-icon" :src="'/download_logo/' + card.logo + '-logo.webp'" :alt="card.showName" />
            <button class="store-btn" type="button" @click="handleDown(card.storeType)">
              <img class="store-icon" :src="card.storeIcon" alt="" />
              <span>{{ card.storeLabel }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Mobile Layout -->
    <div class="for-mobile page">
      <img class="page-bg" :src="bgMobile" alt="" />
      <div class="page-inner">
        <section class="hero">
          <h1 class="headline">{{ t.k001 }}</h1>
          <div class="subtitle-card">
            <p>{{ t.k006 }}</p>
          </div>
        </section>
        <section class="phone-section">
          <img class="phone-img" :src="bgPhone" alt="" />
        </section>
        <section class="bottom-panel">
          <div class="download-cards">
            <div class="download-card" v-for="card in cards" :key="card.storeType">
              <img class="app-icon" :src="'/download_logo/' + card.logo + '-logo.webp'" :alt="card.showName" />
              <button class="store-btn" type="button" @click="handleDown(card.storeType)">
                <img class="store-icon" :src="card.storeIcon" alt="" />
                <span>{{ card.storeLabel }}</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import BaseConfig from '@/config/base.js'
import i18nMixin from './mixins/i18n.mixin'
import docMetaMixin from './mixins/doc-meta.mixin'

const iosAppNames = ['com.fh.whisper.chat', 'com.example.echo']

function mkItem(logo, showName, link, needParams) {
  return link ? { logo: logo, showName: showName, link: link, needParams: needParams } : null
}

const downloadStrategies = {
  vava: (links) => ({
    google: mkItem('vava', 'Vava', links.vava.android, true),
    apple: mkItem('echo', 'Echo', links.echo.ios, false),
  }),
  echo: (links) => ({
    google: mkItem('echo', 'Echo', links.echo.android, true),
    apple: mkItem('echo', 'Echo', links.echo.ios, false),
  }),
  joychat: (links) => ({
    google: mkItem('joychat', 'Joychat', links.joychat.android, true),
    apple: mkItem('joychat', 'Joychat', links.joychat.ios, false),
  }),
  joyu: (links) => ({
    google: mkItem('joyu', 'Joyu', links.joyu.android, true),
    apple: null,
  }),
  kiko: (links) => ({
    google: mkItem('kiko', 'Kiko', links.kiko.android, true),
    apple: mkItem('kiko', 'Kiko', links.kiko.ios, false),
  }),
  vibeo: (links) => ({
    google: mkItem('vibeo', 'Vibeo', links.vibeo.android, true),
    apple: mkItem('vibeo', 'Vibeo', links.vibeo.ios, false),
  }),
  whonow: (links) => ({
    google: mkItem('whonow', 'Whonow', links.whonow.android, true),
    apple: mkItem('whonow', 'Whonow', links.whonow.ios, false),
  }),
}

export default {
  name: 'DownloadAgent',
  mixins: [i18nMixin, docMetaMixin],
  data() {
    return {
      cards: [],
      storeItemMap: {},
      _pagePackage: 'vava',
    }
  },
  computed: {
    appIconSrc() {
      return '/download_logo/' + this._pagePackage + '-logo.webp'
    },
    bgDesktop() {
      try {
        return require('./images/bg_desktop.webp')
      } catch (e) {
        return ''
      }
    },
    bgMobile() {
      try {
        return require('./images/bg_mobile.webp')
      } catch (e) {
        return ''
      }
    },
    bgPhone() {
      try {
        return require('./images/bg_phone_new.webp')
      } catch (e) {
        return ''
      }
    },
    googleIcon() {
      try {
        return require('./images/google_play_icon.webp')
      } catch (e) {
        return ''
      }
    },
    appleIcon() {
      try {
        return require('./images/apple_logo.webp')
      } catch (e) {
        return ''
      }
    },
  },
  created() {
    const query = this.$route.query || {}
    const appName = query.appName || 'com.client.goparty'

    const cfg = this.buildDownloadConfig({
      appName,
      googleIcon: this.googleIcon,
      appleIcon: this.appleIcon,
      t: this.t,
    })
    this._pagePackage = cfg.pagePackage
    this.storeItemMap = cfg.storeItemMap
    this.cards = cfg.cards
  },
  mounted() {
    this.setDocMeta()
  },
  beforeDestroy() {},
  methods: {
    buildDownloadConfig(opts) {
      const { appName, googleIcon, appleIcon, t } = opts
      const packageName = BaseConfig.packageNameMap[appName] || 'vava'
      const fromIos = iosAppNames.indexOf(appName) > -1
      const effectivePackage = packageName === 'echo' && fromIos ? 'vava' : packageName
      const downloadItems = (downloadStrategies[effectivePackage] || downloadStrategies.vava)(BaseConfig.downloadLinks)

      const googleItem = downloadItems.google
      const appleItem = downloadItems.apple
      const storeItemMap = { google: googleItem, apple: appleItem }

      const cards = []
      if (googleItem && googleItem.link) {
        cards.push({ storeType: 'google', logo: googleItem.logo, showName: googleItem.showName, storeIcon: googleIcon, storeLabel: t.k008 })
      }
      if (appleItem && appleItem.link) {
        cards.push({ storeType: 'apple', logo: appleItem.logo, showName: appleItem.showName, storeIcon: appleIcon, storeLabel: t.k009 })
      }

      return {
        pagePackage: effectivePackage || 'vava',
        storeItemMap,
        cards,
      }
    },
    handleDown(storeType) {
      const item = this.storeItemMap[storeType]
      if (!item) return
      let url = item.link
      if (item.needParams && url) {
        url = url + '&referrer=' + encodeURIComponent('utm_source=agent_invite&utm_medium=' + (this.$route.query.code || ''))
      }
      if (url) {
        window.open(url, '_blank')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.download-agent-root {
  --fw-400: 400;
  --fw-500: 500;
  --fw-600: 600;
  --fw-700: 700;
  --fw-800: 800;
  --string-font: 'Roboto-Regular', 'Helvetica Neue', Arial, sans-serif;
  --headline-font: 'PlusJakartaSans-ExtraBoldItalic', 'Roboto-Regular', sans-serif;
  --subtitle-font: 'PlusJakartaSans-Regular', 'Roboto-Regular', sans-serif;
  --number-font: 'Inter-Regular', 'Helvetica Neue', Arial, sans-serif;
  --fs-12: 12px;
  --fs-14: 14px;
  --fs-16: 16px;
  --fs-20: 20px;
  --fs-28: 28px;
  --lh-body-18: 18px;
  --lh-body-20: 20px;
  --lh-headline-36: 36px;
  --lh-title-20: 20px;
  --panel-bg: #1f160b;
  --page-purple: #5c3d18;

  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: var(--page-purple);
  font-family: var(--string-font);

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .for-web.page {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    .page-bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top center;
      pointer-events: none;
      z-index: 1;
    }
    .page-inner {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      flex: none;
      width: 100%;
      max-width: 1281px;
      min-height: 0;
      margin: 0 auto;
      padding-bottom: 212px;
    }
    .hero {
      padding: 36px clamp(16px, calc((100% - 641px) / 2), 320px) 0;
    }
    .headline {
      padding: 10px 0;
      font-family: var(--headline-font);
      font-size: var(--fs-28);
      font-weight: var(--fw-800);
      font-style: italic;
      line-height: var(--lh-headline-36);
      color: #ffffff;
      text-align: left;
    }
    .subtitle-card {
      margin-top: 4px;
      padding: 8px 12px;
      border: 1px solid #ffffff;
      border-radius: 12px;
      background: linear-gradient(180deg, rgba(240, 170, 120, 0.8) 0%, rgba(240, 170, 120, 0.4) 100%);
      backdrop-filter: blur(5px);
    }
    .subtitle-card p {
      font-family: var(--subtitle-font);
      font-size: var(--fs-14);
      font-weight: var(--fw-400);
      line-height: var(--lh-body-18);
      color: #ffffff;
    }
    .phone-section {
      position: relative;
      z-index: 1;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      flex: none;
      padding: 0;
      margin-bottom: -212px;
    }
    .phone-img {
      width: 100%;
      max-width: 375px;
      height: auto;
      display: block;
    }
    .bottom-panel {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10;
      width: 100%;
      margin-top: 0;
      padding: 24px 0 0;
      background: var(--panel-bg);
      border: 1px solid #ffffff;
      border-bottom: none;
      border-radius: 16px 16px 0 0;
      --web-cards-row-width: calc(640px + 4px);
    }
    .download-cards {
      display: flex;
      gap: 4px;
      width: var(--web-cards-row-width);
      max-width: calc(100% - 32px);
      margin: 0 auto;
      padding: 0;
      overflow-x: visible;
      justify-content: center;
    }
    .download-card {
      flex: 0 0 320px;
      width: 320px;
      max-width: 320px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 12px;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.1);
    }
    .download-card .app-icon {
      width: 44px;
      height: 44px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }
    .store-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      flex: 1;
      min-width: 0;
      padding: 12px 24px;
      border: none;
      border-radius: 36px;
      background: #ffffff;
      color: #333333;
      font-family: var(--string-font);
      font-size: var(--fs-16);
      font-weight: var(--fw-400);
      line-height: var(--lh-title-20);
      white-space: nowrap;
      cursor: pointer;
    }
    .store-btn .store-icon {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
  }

  .for-mobile.page {
    position: relative;
    display: none;
    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    .page-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
      pointer-events: none;
      z-index: 1;
    }
    .page-inner {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      min-height: auto;
    }
    .hero {
      padding: 16px 16px 0;
    }
    .headline {
      padding: 10px 0;
      font-family: var(--headline-font);
      font-size: var(--fs-28);
      font-weight: var(--fw-800);
      font-style: italic;
      line-height: var(--lh-headline-36);
      color: #ffffff;
      text-align: left;
    }
    .subtitle-card {
      margin-top: 4px;
      padding: 8px 12px;
      border: 1px solid #ffffff;
      border-radius: 12px;
      background: linear-gradient(180deg, rgba(240, 170, 120, 0.8) 0%, rgba(240, 170, 120, 0.4) 100%);
      backdrop-filter: blur(5px);
    }
    .subtitle-card p {
      font-family: var(--subtitle-font);
      font-size: var(--fs-14);
      font-weight: var(--fw-400);
      line-height: var(--lh-body-18);
      color: #ffffff;
    }
    .phone-section {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      flex: none;
      padding: 0;
    }
    .phone-img {
      width: 100%;
      max-width: 375px;
      height: auto;
      display: block;
    }
    .bottom-panel {
      position: relative;
      z-index: 3;
      margin-top: -150px;
      padding: 24px 0 0;
      background: var(--panel-bg);
      border: 1px solid #ffffff;
      border-bottom: none;
      border-radius: 16px 16px 0 0;
    }
    .download-cards {
      display: flex;
      gap: 4px;
      padding: 0 16px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      width: auto;
      max-width: none;
      margin: 0;
      justify-content: stretch;
    }
    .download-card {
      flex: 1;
      flex-shrink: 0;
      min-width: 150px;
      width: auto;
      max-width: none;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.1);
    }
    .download-card .app-icon {
      width: 44px;
      height: 44px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }
    .store-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 100%;
      min-width: 0;
      padding: 12px 24px;
      border: none;
      border-radius: 36px;
      background: #ffffff;
      color: #333333;
      font-family: var(--string-font);
      font-size: var(--fs-16);
      font-weight: var(--fw-400);
      line-height: var(--lh-title-20);
      white-space: nowrap;
      cursor: pointer;
    }
    .store-btn .store-icon {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
  }
}

@media screen and (max-width: 767px) {
  .download-agent-root .for-web.page {
    display: none;
  }
  .download-agent-root .for-mobile.page {
    display: block;
  }
}
</style>
